import React, { useEffect, useState } from 'react';
import { Database, Activity, LogOut, User } from 'lucide-react';
import coolbitLogoDefault from '../assets/coolbit_logo.png';
import triaraLogoDefault from '../assets/triara_logo.png';
import LanguageSwitcher from './LanguageSwitcher';
import { useI18n } from '../i18n/i18n';
import { strapi, STRAPI_URL } from '../lib/strapi';

interface HeaderProps {
  onLogout?: () => void;
  onLogin?: () => void;
  userLabel?: string;
}

const Header: React.FC<HeaderProps> = ({ onLogout, onLogin, userLabel }) => {
  const { t } = useI18n();
  const [dynamicConfig, setDynamicConfig] = useState<any>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await strapi.get('/api/general-config?populate=*');
        if (response.data && response.data.data) {
          setDynamicConfig(response.data.data);
        }
      } catch (err) {
        console.warn("Strapi no disponible, usando configuración local.");
      }
    };
    fetchConfig();
  }, []);

  const getLogoUrl = (strapiMedia: any, defaultLogo: string) => {
    const url = strapiMedia?.url;
    if (url) {
      // Usamos la URL centralizada de lib/strapi.ts
      return `${STRAPI_URL}${url}`;
    }
    return defaultLogo;
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <img 
                src={getLogoUrl(dynamicConfig?.logo_coolbit, coolbitLogoDefault)} 
                alt="Coolbit AI logo" 
                className="h-10 w-auto" 
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {dynamicConfig?.app_title || 'Coolbit AI'}
                </h1>
                <p className="text-sm text-gray-500">{t('header.subtitle')}</p>
              </div>
            </div>
            <div className="border-l border-gray-300 h-10 mx-2"></div>
            <img 
              src={getLogoUrl(dynamicConfig?.logo_cliente, triaraLogoDefault)} 
              alt="Cliente logo" 
              className="h-10 w-auto" 
            />
          </div>
          
          <div className="flex items-center space-x-6">
            <LanguageSwitcher />
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Database className="h-4 w-4" />
                <span>{t('header.supabase')}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Activity className="h-4 w-4" />
                <span>{t('header.analytics')}</span>
              </div>
            </div>
            
            {onLogout ? (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{userLabel || 'User'}</span>
                </div>
                <button
                  onClick={onLogout}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t('header.signout')}</span>
                </button>
              </div>
            ) : onLogin ? (
              <div className="flex items-center">
                <button
                  onClick={onLogin}
                  className="px-3 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  {t('login.signin')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
